/**
 * API error helpers.
 *
 * FastAPI backends we call return `detail` in three shapes:
 *   1. Plain string  →  "You can't do that"
 *   2. Object (custom exception)
 *        { code: 'INSUFFICIENT_CREDITS', message: '...', balance, needed }
 *        { code: 'ADMIN_ONLY', message: '...' }
 *      → we prefer `detail.message`, then `detail.detail`, then JSON stringify.
 *   3. Array (Pydantic 422 validation error)
 *        [ { loc: [...], msg: 'field required', type: '...' }, ... ]
 *      → we take the first item's `msg`.
 *
 * Any call site that fed a raw `detail` into `toast.error()` was rendering
 * "[object Object]" or dropping the message on the floor. Route every
 * error toast / setState through this helper so the founder sees the
 * actual reason (e.g. "You need 2 credits — you have 0").
 */

import { toast } from "sonner";

/**
 * Extract a human-readable string from an axios error.
 * @param {unknown} err
 * @param {string}  fallback
 * @returns {string}
 */
export function extractErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      return first.msg || first.message || first.detail || fallback;
    }
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.detail || detail.error || fallback;
  }
  const dataMsg = err?.response?.data?.message;
  if (typeof dataMsg === "string" && dataMsg.trim()) return dataMsg;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

/**
 * Build a structured debug payload for support triage. All fields optional;
 * missing values are omitted so the copied blob stays compact.
 */
export function buildErrorContext(err, ctx = {}) {
  const detail = err?.response?.data?.detail;
  const code =
    (detail && typeof detail === "object" && !Array.isArray(detail) && detail.code) ||
    err?.response?.data?.code ||
    undefined;

  const config = err?.config || {};
  // Include the axios baseURL so the copied endpoint is a full '/api/...' path,
  // not the relative sub-path axios stores in error.config.url.
  let endpoint = ((config.baseURL || "") + (config.url || "")) || undefined;
  if (endpoint) {
    // Strip origin so the blob stays compact and shareable.
    endpoint = endpoint.replace(/^https?:\/\/[^/]+/, "");
  }

  const payload = {
    ts: new Date().toISOString(),
    status: err?.response?.status,
    code,
    endpoint,
    method: config.method ? String(config.method).toUpperCase() : undefined,
    message: extractErrorMessage(err, undefined),
    request_id:
      err?.response?.headers?.["x-request-id"] ||
      err?.response?.headers?.["x-amzn-requestid"] ||
      undefined,
    ...ctx,
  };
  // Remove undefined keys for a clean copy.
  Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
  return payload;
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to legacy path.
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Toast an API error with either a "Buy credits" CTA (for 402
 * INSUFFICIENT_CREDITS) or a "Copy details" action that puts a structured
 * debug blob on the clipboard for support triage.
 *
 * @param {unknown} err
 * @param {string} fallback
 * @param {object} [opts]
 * @param {object} [opts.context]  Extra fields to include in the copied blob
 *                                  (e.g. { deck_id, slide_num }).
 * @param {function} [opts.onBuyCredits]  Handler for the 402 CTA. If omitted,
 *                                        the "Buy credits" action is not shown.
 * @param {number} [opts.duration]  Toast duration in ms.
 */
export function toastApiError(err, fallback, opts = {}) {
  const { context = {}, duration = 8000 } = opts;
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const isCreditShortage =
    status === 402 &&
    detail &&
    typeof detail === "object" &&
    detail.code === "INSUFFICIENT_CREDITS";

  const message = extractErrorMessage(err, fallback);

  if (isCreditShortage) {
    // Handled globally by the api.js 402 interceptor, which already showed
    // a single "Buy credits" CTA toast (deduped via toast id). Don't
    // double-toast here.
    return;
  }

  const debugBlob = buildErrorContext(err, context);
  toast.error(message, {
    duration,
    action: {
      label: "Copy details",
      onClick: async () => {
        const ok = await copyToClipboard(JSON.stringify(debugBlob, null, 2));
        if (ok) {
          toast.success("Error details copied — paste into support.", { duration: 4000 });
        } else {
          toast.error("Could not access clipboard. Please copy manually.", { duration: 4000 });
        }
      },
    },
  });
}
